import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useOmlMutations } from '@/hooks/useOml';
import { cn } from '@/lib/utils';

/**
 * Campo restringido (§7.3, §9.3): bloque con candado, fondo diferenciado y
 * acción de solicitar acceso — nunca oculto sin explicación. La credencial no
 * admite solicitud (`requestable=false`).
 */
export function LockedField({ label, accountId, field, requestable = true, compact = false, className }: {
  label: string; accountId: string; field?: string; requestable?: boolean; compact?: boolean; className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className={cn('rounded-md border border-dashed border-amber-300/70 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-800', compact ? 'px-2 py-1 inline-flex items-center gap-2 text-xs' : 'p-3 flex items-center justify-between gap-3', className)}>
      <div className="flex items-center gap-2 min-w-0">
        <Lock className="h-3.5 w-3.5 text-amber-700 shrink-0" />
        <span className={cn('truncate', compact ? '' : 'text-sm')}>
          <span className="font-medium">{label}</span>
          {!compact && <span className="text-muted-foreground"> · {t('oml.locked.hint', 'Campo restringido: existe, pero no lo ves por permisos.')}</span>}
        </span>
      </div>
      {requestable ? (
        <Button size="sm" variant="outline" className="shrink-0 h-7" onClick={() => setOpen(true)}>{t('oml.locked.request', 'Solicitar acceso')}</Button>
      ) : (
        !compact && <span className="text-xs text-muted-foreground shrink-0">{t('oml.locked.no_request', 'No se solicita por excepción: vive en el gestor de contraseñas.')}</span>
      )}
      {open && <RequestAccessDialog open={open} onOpenChange={setOpen} accountId={accountId} field={field} scope="commercial" />}
    </div>
  );
}

export function RequestAccessDialog({ open, onOpenChange, accountId, field, scope }: { open: boolean; onOpenChange: (o: boolean) => void; accountId: string | null; field?: string; scope: 'commercial' | 'export' }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const { requestAccess } = useOmlMutations();
  const submit = async () => {
    await requestAccess.mutateAsync({ scope, accountId, field, reason });
    onOpenChange(false);
    setReason('');
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{scope === 'export' ? t('oml.request.title_export', 'Solicitar permiso de exportación') : t('oml.request.title', 'Solicitar acceso a un campo restringido')}</DialogTitle>
          <DialogDescription>{t('oml.request.desc', 'El data owner del apartado recibe la solicitud y la aprueba con una fecha de vencimiento. Si no responde en 5 días hábiles, escala al Lead OMC. Nunca se aprueba sola.')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="oml-req-reason">{t('oml.request.reason', 'Motivo')}</Label>
          <Textarea id="oml-req-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={4} placeholder={t('oml.request.reason_ph', 'Para qué necesitás el dato y hasta cuándo')} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel', 'Cancelar')}</Button>
          <Button onClick={submit} disabled={reason.trim().length < 5 || requestAccess.isPending}>{t('oml.request.send', 'Enviar solicitud')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
